import React, { useCallback, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { stripeCheckoutAPI } from "../apis/apiStripe.js";
import { CartContext } from "../contexts/CartContext.js";
import { AuthContext } from "../contexts/AuthContext.js";
import { useNavigate } from "react-router-dom";

/**
 * Clé publique de test Stripe
 */
const stripePromise = loadStripe(
  "pk_live_51P7HjX0179peklSeRTty2W6aPULnYzQRkhs2s2MLmC4kbc3LvR8XNniZES02OfniiYHkCllQwQirO7ZVK6S4x0uA00fskOF9RB"
);

const CheckoutPage = () => {
  const { cartItems } = useContext(CartContext);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchClientSecret = useCallback(async () => {
    const shipping = JSON.parse(localStorage.getItem("deliveryAddress"));

    if (!shipping) {
      navigate('/checkout/shipping-address')
    }

    const isPickupPoint = shipping.pickup_point;
    const haveNoSignature = shipping.haveNoSignature;

    const aggregatedCartItems = cartItems.reduce((acc, currentItem) => {
      if (currentItem.price?.type === "recurring") {
        const existingItem = acc.find(
          (item) => item.productId === currentItem.productId && item.price?.type === "recurring"
        );

        if (existingItem) {
          existingItem.quantity += currentItem.quantity || 1;
        } else {
          acc.push({
            ...currentItem,
            quantity: currentItem.quantity || 1,
            // selected_size: currentItem.metadata.selectedSize
          });
        }
      } else {
        acc.push({
          ...currentItem,
          quantity: currentItem.quantity || 1,
        });
      }

      return acc;
    }, []);

    const lineItems = aggregatedCartItems.flatMap((item) => {
      const quantity = item.quantity;

      if (item.price?.type === "recurring") {
        const recurringLine = {
          price: item.price.id,
          quantity,
          // selected_size: item.metadata.selectedSize,
        };

        const rawFee = item.price.metadata?.activation_fees || "0";
        const activationFee = parseFloat(rawFee.replace(",", "."));
        
        if (activationFee > 0) {
          return [
            recurringLine,
            {
              price_data: {
                currency: item.price.currency,
                product_data: {
                  name: `Frais d'activation - ${item.name}`,
                },
                unit_amount: Math.round(activationFee * 100),
              },
              quantity,
            },
          ];
        }

        return [recurringLine];
      }

      const itemModified = {
        price: item.price.id,
        quantity,
      };

      if (item.metadata.selectedSize) {
        itemModified.selected_size = item.metadata.selectedSize
      }

      return [
        itemModified
      ];
    });

    const isSubscription = cartItems.some((item) => item.price?.type === "recurring");
    const sessionMode = isSubscription ? "subscription" : "payment";
    const shippingFee = isPickupPoint ? 0 : haveNoSignature ? 0 : 0;

    if(isSubscription) {
        lineItems.push({
          price_data: {
          currency: "eur",
          product_data: {
            name: "Frais de livraison (paiement unique)",
          },
          unit_amount: shippingFee,
        },
        quantity: 1,
      });
    }
  
    const shippingOptions = [
      {
        shipping_rate_data: {
          display_name: isPickupPoint ? "Retrait en point relais" : "Livraison à domicile",
          type: "fixed_amount",
          fixed_amount: {
            amount: isPickupPoint ? 0 : (haveNoSignature ? 0 : 0), // Montant en centimes
            currency: "eur",
          },
          delivery_estimate: {
            minimum: { unit: "business_day", value: 2 },
            maximum: { unit: "business_day", value: 3 },
          },
        },
      },
    ];

    let metadata;

    if (isPickupPoint) {
      metadata = {
        street: shipping.street,
        // street_number: shipping.street_number,
        city: shipping.city,
        postal_code: shipping.postal_code,
        country_code: shipping.country_code,
        shipping_name: `${shipping.first_name} ${shipping.last_name}`,
        shipping_method_id: shipping.shipping_method_id,
        weight: shipping.weight,
        to_service_point: shipping.service_point_id,
        phone_number: shipping.phone_number,
      };
    } else {
      metadata = {
        shipping_name: `${shipping.first_name} ${shipping.last_name}`,
        street: shipping.street,
        // street_number: shipping.street_number,
        line2: shipping.line2 || '',
        postal_code: shipping.postal_code,
        city: shipping.city,
        country: shipping.country,
        country_code: shipping.country_code,
        shipping_method_id: shipping.shipping_method_id,
        weight: shipping.weight,
        phone_number: shipping.phone_number,
      };
    }

    const sessionBody = {
      payment_method_types: ["card", "paypal", "klarna", "revolut_pay"],
      mode: sessionMode,
      line_items: lineItems,
      metadata: metadata,
      customer: {
        email: shipping.email,
        name: shipping.first_name + ' ' + shipping.last_name,
        phone: shipping.phone_number
      },
      allow_promotion_codes: true,
    };
  
    if(!isSubscription) {
      sessionBody.shipping_options = shippingOptions;
    } else {
      sessionBody.invoice_creation = true;
    }
console.log(sessionBody);
    try {
      const data = await stripeCheckoutAPI(sessionBody);
      return data.clientSecret;
    } catch (error) {
      console.error("Erreur lors de la création de la session Stripe :", error);
      throw error;
    }
    // eslint-disable-next-line
  }, [cartItems, authCtx.connected, authCtx?.currentUser]);
  
  const options = { fetchClientSecret };  

  return (
    <div id="checkout" style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem" }}>
      <h2 style={{textAlign: "center"}}>Paiement sécurisé</h2>
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default CheckoutPage;
